import { isoStringToDate } from "../shared/date-format";
import { ColumnDefinition, EntityInterface } from "./Table";

/** Transaction source types */
export type TransactionSource = 'Pending Payment' | 'Payment' | 'Purchase' | 'Fee';

/**
 * TransactionDetailInterface defines the structure of a transaction detail object
 * from the backend API. Uses string dates that need to be parsed.
 */
export interface TransactionDetailInterface {
    transaction_reference_no: string;
    acctrefno: string;
    loan_number: string;
    transrefno: string | null;
    date_paid: string | null;
    transaction_date: string | null;
    effective_date: string | null;
    gl_date: string | null;
    payment_reference_no: string | null;
    payment_amount: number;
    transaction_code: string | null;
    transaction_amount: number;
    source: TransactionSource;
    request_memo: string | null;
    request_file_ids: number[];
    is_pending: boolean;
}

/**
 * TransactionDetail class representing a detailed transaction record
 */
export class TransactionDetail implements EntityInterface {
    /**
     * Constructor for the TransactionDetail class
     */
    constructor(
        public transaction_reference_no: string,
        public acctrefno: string,
        public loan_number: string,
        public transrefno: string | null,
        public date_paid: Date | null,
        public transaction_date: Date | null,
        public effective_date: Date | null,
        public gl_date: Date | null,
        public payment_reference_no: string | null,
        public payment_amount: number,
        public transaction_code: string | null,
        public transaction_amount: number,
        public source: TransactionSource,
        public request_memo: string | null,
        public request_file_ids: number[],
        public is_pending: boolean,
    ) { }

    /**
     * Gets the ID for EntityInterface compliance - uses transaction_reference_no
     */
    get id(): string {
        return this.transaction_reference_no;
    }

    /**
     * Creates a copy of the current TransactionDetail instance
     * 
     * @returns A copy of the current TransactionDetail instance
     */
    copyOf(): TransactionDetail {
        return new TransactionDetail(
            this.transaction_reference_no,
            this.acctrefno,
            this.loan_number,
            this.transrefno,
            this.date_paid,
            this.transaction_date,
            this.effective_date,
            this.gl_date,
            this.payment_reference_no,
            this.payment_amount,
            this.transaction_code,
            this.transaction_amount,
            this.source,
            this.request_memo,
            this.request_file_ids,
            this.is_pending,
        );
    }

    /**
     * Return true if the current TransactionDetail instance is equal to the given transaction
     * 
     * @param other The other transaction detail to compare with
     * @returns True if the transaction details are equal, false otherwise
     */
    equals(other: TransactionDetail): boolean {
        return this.transaction_reference_no === other.transaction_reference_no &&
            this.acctrefno === other.acctrefno &&
            this.loan_number === other.loan_number &&
            this.transrefno === other.transrefno &&
            this.date_paid?.getTime() === other.date_paid?.getTime() &&
            this.transaction_date?.getTime() === other.transaction_date?.getTime() &&
            this.effective_date?.getTime() === other.effective_date?.getTime() &&
            this.gl_date?.getTime() === other.gl_date?.getTime() &&
            this.payment_reference_no === other.payment_reference_no &&
            this.payment_amount === other.payment_amount &&
            this.transaction_code === other.transaction_code &&
            this.transaction_amount === other.transaction_amount &&
            this.source === other.source &&
            this.request_memo === other.request_memo &&
            JSON.stringify(this.request_file_ids) === JSON.stringify(other.request_file_ids) &&
            this.is_pending === other.is_pending;
    }

    /**
     * Returns a string representation of the TransactionDetail instance for filtering
     * 
     * @param defs The column definitions to use for formatting
     * @returns A string representation of the TransactionDetail instance
     */
    toString(defs: ColumnDefinition<any>[]): string {
        let str = "";
        for (let col of defs) {
            if (col.toStringTransform != undefined) {
                str += col.toStringTransform((this as any)[col.field]) + "█";
            } else {
                str += (this as any)[col.field];
            }
        }

        return str.toLowerCase();
    }

    /**
     * Gets the formatted payment amount as a currency string
     * 
     * @returns The payment amount formatted as currency
     */
    get formattedPaymentAmount(): string {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD'
        }).format(this.payment_amount);
    }

    /**
     * Gets the formatted transaction amount as a currency string
     * 
     * @returns The transaction amount formatted as currency
     */
    get formattedTransactionAmount(): string {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD'
        }).format(this.transaction_amount);
    }
}

/** Mapper for converting between TransactionDetailInterface and TransactionDetail */
export class TransactionDetailMapper {
    /**
     * Converts a TransactionDetail model to a TransactionDetailInterface DTO
     * 
     * @param transaction The TransactionDetail model to convert
     * @returns The converted TransactionDetailInterface DTO
     */
    static toDto(transaction: TransactionDetail): TransactionDetailInterface {
        return {
            transaction_reference_no: transaction.transaction_reference_no,
            acctrefno: transaction.acctrefno,
            loan_number: transaction.loan_number,
            transrefno: transaction.transrefno,
            date_paid: transaction.date_paid?.toISOString() || null,
            transaction_date: transaction.transaction_date?.toISOString() || null,
            effective_date: transaction.effective_date?.toISOString() || null,
            gl_date: transaction.gl_date?.toISOString() || null,
            payment_reference_no: transaction.payment_reference_no,
            payment_amount: transaction.payment_amount,
            transaction_code: transaction.transaction_code,
            transaction_amount: transaction.transaction_amount,
            source: transaction.source,
            request_memo: transaction.request_memo,
            request_file_ids: transaction.request_file_ids,
            is_pending: transaction.is_pending,
        };
    }

    /**
     * Converts a TransactionDetailInterface DTO to a TransactionDetail model
     * 
     * @param dto The TransactionDetailInterface DTO to convert
     * @returns The converted TransactionDetail model
     */
    static toModel(dto: TransactionDetailInterface): TransactionDetail {
        return new TransactionDetail(
            dto.transaction_reference_no,
            dto.acctrefno,
            dto.loan_number,
            dto.transrefno,
            isoStringToDate(dto.date_paid),
            isoStringToDate(dto.transaction_date),
            isoStringToDate(dto.effective_date),
            isoStringToDate(dto.gl_date),
            dto.payment_reference_no,
            dto.payment_amount,
            dto.transaction_code,
            dto.transaction_amount,
            dto.source,
            dto.request_memo,
            dto.request_file_ids,
            dto.is_pending,
        );
    }

    
}
